import { AmpPlugin, create, DateTime, easepick, RangePlugin } from "@easepick/bundle";
import { BlackoutPlugin } from "../../../EasePick/blackout-plugin/BlackoutPlugin";
import { LockPartlyPlugin } from "../../../EasePick/lock-partly-plugin";
import { TLockPartlyRange } from "../../../EasePick/lock-partly-plugin/interface";
import { SELECTORS as CART_MANAGER_SELECTORS } from "../../Cart/Constants";

import { BookThatApi } from "../../../BookThatApp/API";
import LoaderOverlay from "../../LoaderOverlay";
import {
    addBlackoutTooltip,
    holidayBlackouts,
    holidayBlackoutsAllYear,
    inAYear,
    pickedRange,
    seasonEnd,
    seasonStart,
    today
} from "../Helper/DateRangePicker";
import { Checkbox } from "../../../Components/Form/Checkbox";
import { Dropdown } from "../../../Components/Form/Dropdown";
import { clamp } from "../../../Utils/Number";
import { isSunOrHoliday } from "feiertagejs";
import { regionsForHolidays } from "../Config";
import { formatMoneyIntl } from "../../../Utils/Money";
import { Toast } from "../../../Components/Dialog/Toast";
import { HTTPError } from "../../../Exceptions/HTTPError";
import { preventScroll, releaseScroll } from "../../../Utils/PreventScroll";
import { IBTAProduct } from "../../../Interfaces/BTA";

// https://easepick.com/guide/
// https://easepick.com/packages/datetime.html
// https://easepick.com/packages/lock-plugin.html

/**
 * DateTime.(inArray|isBetween) inclusivity argument is not documented
 * @see: https://github.com/easepick/easepick/blob/4a49a0f029551aabb9f890d8458b0d7a2046842e/packages/datetime/src/index.ts#L290
 * "(" : date is > midnight
 * "[" : date is >= midnight
 * ")" : date is < midnight
 * "]" : date is <= midnight
 *
 * argument is a combined string like e.g. "[)" : date is >= midnight of date1 and is < midnight of date2
 */

export const pickerElement = document.getElementById("date-range-picker");
export const trigger = document.querySelector<HTMLElement>(".pdp-detail #date-range-picker-trigger");
const loaderOverlayContainer = trigger?.closest<HTMLElement>(".detail-cta");
const SELECTORS = {
    wrapper: ".detail-cta",
    startLabel: ".date.start .label",
    endLabel: ".date.end .label",
    priceBox: ":scope section.price",
    priceTemplate: ":scope template#rental-price",
    timePicker: ':scope x-dropdown[name="time"]',
    timePickerEnd: ':scope x-dropdown[name="time-end"]',
    checkboxShort: ':scope x-checkbox[name="short"]',
    indicatorWarning: ".indicator-warning",
    unavailableMessage: ".unavailable"
};
const DATE_FORMATS = {
    date: "YYYY-MM-DD",
    time: "hh:mm",
    dateTime: "YYYY-MM-DD hh:mm",
    presentation: "MM-DD-YY"
};
export const EVENTS = {
    change: "change",
    click: "click",
    clear: "clear",
    select: "select"
};

const shortTariffDurationHours = 3;
const lagTimeHours = 2;
const openingHours = {
    weekdays: {
        start: 10,
        end: 18
    },
    saturday: {
        start: 10,
        end: 14
    }
};

export interface IRentalProperties {
    from?: string;
    to?: string;
    _tariff?: string;
    _price_per_day?: number;
    _rental_duration?: number;
    _insurance_quantity?: number;
    _accessories_quantity?: number;
    _rental_pickup?: string;
}

export class RentalDatePicker {
    picker: create;
    cta: HTMLButtonElement;
    detailsWrapper: HTMLElement;
    unavailableMessage: HTMLSpanElement;
    startPresentation: HTMLSpanElement;
    endPresentation: HTMLSpanElement;
    show: () => void;
    cancel: () => void;
    select: () => void;
    reset: () => void;
    bookedDates: DateTime[];
    bookingRanges: TLockPartlyRange[];
    protected variantBookingRanges: TLockPartlyRange[];
    product: IBTAProduct[];
    api: BookThatApi;
    priceTemplate: HTMLTemplateElement;
    priceBox: HTMLElement;
    timePicker: Dropdown;
    timePickerEnd: Dropdown;
    checkboxShort: Checkbox | HTMLInputElement;
    locale: string = `${Shopify.locale}-${Shopify.country}`;
    private loaderDatePicker: LoaderOverlay;
    private abortController: AbortController;
    private showWarningToastMessage = true;
    private indicatorWarning: HTMLSpanElement;
    private toastMessage: string;

    durationShortHours: number = shortTariffDurationHours;
    durationShortMSeconds: number = shortTariffDurationHours * 60 * 60 * 1000;
    openingHours: { [key: number]: { start: number; end: number } } = {
        1: {
            start: 10,
            end: 18
        },
        2: {
            start: 10,
            end: 18
        },
        3: {
            start: 10,
            end: 18
        },
        4: {
            start: 10,
            end: 18
        },
        5: {
            start: 10,
            end: 18
        },
        6: {
            start: 9,
            end: 14
        }
    };

    constructor() {
        this.init().then(/* suppress ignored promise warning */);
    }

    /**
     * initialize
     */
    async init() {
        this.api = new BookThatApi();
        this.loaderDatePicker = new LoaderOverlay(loaderOverlayContainer, 0.7);
        try {
            this.initDateFormat().initListener().initElements();
            this.loaderDatePicker.render();
            await this.initData();
            this.initPicker().addPickerListeners().disableHandler();
        } catch (error) {
            if (error instanceof HTTPError) {
                new Toast({
                    title: window.SuccessMessages.btaBlocksRequest.errorTitle,
                    content: window.SuccessMessages.btaBlocksRequest.errorMessage,
                    type: Toast.TYPE_ERROR
                })?.render();
                trigger.classList.add("disabled");
            } else {
                console.error(error);
            }
        } finally {
            this.loaderDatePicker.destroy();
        }
    }

    /**
     * fetch and initialize data
     */
    async initData() {
        try {
            await this.loadProduct();
            await this.loadBookingRanges();

            // keep for testing... time is in UTC!
            // this.bookingRanges = [];
            //
            // this.bookingRanges.push({
            //     start: new DateTime(new Date('2023-05-02T12:00:00.000Z')),
            //     end: new DateTime(new Date('2023-05-03T12:00:00.000Z'))
            // });
            //
            // this.bookingRanges.push({
            //     start: new DateTime(new Date('2023-05-09T11:00:00.000Z')),
            //     end: new DateTime(new Date('2023-05-12T11:00:00.000Z'))
            // });
            //
            // this.bookingRanges.push({
            //     start: new DateTime(new Date('2023-05-22T09:00:00.000Z')),
            //     end: new DateTime(new Date('2023-05-23T09:00:00.000Z'))
            // });
            //
            // this.bookingRanges.push({
            //     start: new DateTime(new Date('2023-05-24T15:00:00.000Z')),
            //     end: new DateTime(new Date('2023-05-25T15:00:00.000Z'))
            // });
            //
            // this.bookingRanges.push({
            //     start: new DateTime(new Date('2023-06-17T11:00:00.000Z')),
            //     end: new DateTime(new Date('2023-06-20T11:00:00.000Z'))
            // });
            this.sortBookingRanges().createBookedDatesArray();
        } catch (error) {
            this.showGeneralError();
        }
    }

    /**
     * initialize elements
     */
    initElements() {
        this.detailsWrapper = trigger.closest<HTMLElement>(SELECTORS.wrapper);
        this.cta = this.detailsWrapper.querySelector<HTMLButtonElement>(CART_MANAGER_SELECTORS.ADD);
        this.startPresentation = trigger.querySelector<HTMLSpanElement>(SELECTORS.startLabel);
        this.endPresentation = trigger.querySelector<HTMLSpanElement>(SELECTORS.endLabel);
        this.priceTemplate = this.detailsWrapper.querySelector(SELECTORS.priceTemplate);
        this.priceBox = this.detailsWrapper.querySelector(SELECTORS.priceBox);
        this.timePicker = this.detailsWrapper.querySelector(SELECTORS.timePicker);
        this.timePickerEnd = this.detailsWrapper.querySelector(SELECTORS.timePickerEnd);
        this.indicatorWarning = this.detailsWrapper.querySelector(SELECTORS.indicatorWarning);
        this.unavailableMessage = this.detailsWrapper.querySelector(SELECTORS.unavailableMessage);

        // since short tariff should not be available atm hence no x-checkbox is rendered we mock it with a native checkbox
        this.checkboxShort = document.createElement("input"); //this.detailsWrapper.querySelector(SELECTORS.checkboxShort);
        this.checkboxShort.type = "checkbox";

        if (pickedRange.start && pickedRange.end) {
            trigger.dataset.start = new DateTime(pickedRange.start).format(DATE_FORMATS.date);
            trigger.dataset.end = new DateTime(pickedRange.end).format(DATE_FORMATS.date);
            this.startPresentation.dataset.date = new DateTime(pickedRange.start).format(DATE_FORMATS.presentation);
            this.endPresentation.dataset.date = new DateTime(pickedRange.end).format(DATE_FORMATS.presentation);
        }
        this.resetUi();
        return this;
    }

    /**
     * initialize presentation date format
     */
    initDateFormat() {
        DATE_FORMATS.presentation = trigger.dataset.dateFormat;
        return this;
    }

    /**
     * initialize event listeners
     */
    initListener() {
        this.show = this.showHandler.bind(this);
        this.cancel = this.cancelHandler.bind(this);
        this.select = this.selectHandler.bind(this);
        this.reset = this.resetHandler.bind(this);
        return this;
    }

    /**
     * add event listeners to picker elements
     */
    addPickerListeners() {
        if (this.isProductAvailable) {
            this.picker.on(EVENTS.click, this.cancel);
            this.picker.on(EVENTS.select, this.select);
            this.picker.on(EVENTS.clear, this.reset);
            this.indicatorWarning.addEventListener("click", () => this.showWarning());
        }
        return this;
    }

    /**
     * show picker
     */
    showHandler() {
        this.showWarningToastMessage = true;
        trigger.dataset.start && this.picker.setStartDate(trigger.dataset.start);
        trigger.dataset.end && this.picker.setEndDate(trigger.dataset.end);
        if (!this.picker.getStartDate()) {
            this.picker.gotoDate(today.isAfter(seasonStart) ? today : seasonStart);
        }
        this.picker.show();
    }

    /**
     * cancel if clicked outside of the picker
     * @param e
     */
    cancelHandler(e: PointerEvent) {
        if (e.target === this.picker.ui.container) {
            this.picker.hide();
        }
    }

    /**
     * handle date selection
     */
    selectHandler() {
        const startDate = this.picker.getStartDate();
        if (startDate) {
            this.updateUiAndProperties();
            const properties = this.properties;
            this.disabledCta = false;
            const duration = this.end.diff(this.start);
            const variant = this.variants.filter((v) => v.days <= duration).pop();
            properties._rental_duration = Math.max(this.end.diff(this.start), 1);
            properties._price_per_day = this.prices.filter((v) => v.days <= duration).pop().price;
            properties._tariff = variant.tariff;
            this.quantity = properties._rental_duration;
            this.properties = properties;
            this.variant = variant.id;
            this.price = properties._price_per_day * properties._rental_duration;
        }
    }

    /**
     * handle picker reset
     */
    resetHandler() {
        this.start = "";
        this.end = "";
        this.disabledCta = true;
        this.quantity = 1;
        this.variant = this.variants.shift().id;
        this.properties = {};
        this.price = 0;
        this.resetUi();
    }

    disableHandler() {
        if (
            !this.isProductAvailable ||
            !trigger.dataset.isAvailableInStore ||
            trigger.dataset.isAvailableInStore === "false"
        ) {
            trigger.classList.add("disabled");
            this.unavailableMessage.classList.remove("hide-me");
            trigger.removeEventListener(EVENTS.click, this.show);
        } else {
            trigger.classList.remove("disabled");
            this.unavailableMessage.classList.add("hide-me");
            trigger.addEventListener(EVENTS.click, this.show);
        }

        return this;
    }

    /**
     * reset ui to its initial state
     */
    resetUi() {
        this.warning = false;
        this.toastMessage = "";
        this.checkboxShort.removeEventListener("change", this.select);
        this.checkboxShort.checked = false;
        this.checkboxShort.addEventListener("change", this.select);
        const date = new DateTime();
        date.subtract(1);
        const firstAvailableStartDate = this.getFirstAvailableDate(date);
        const firstAvailableStartDay = firstAvailableStartDate.getDay();
        this.disableTimePickerOptions(firstAvailableStartDay, this.openingHours[firstAvailableStartDay].start);
        this.timePicker.removeEventListener("change", this.select);
        this.timePicker.value = `${this.openingHours[firstAvailableStartDay].start.toString().padStart(2, "0")}:00`;
        this.timePicker.addEventListener("change", this.select);
        const firstAvailableEndDate = this.getFirstAvailableDate(firstAvailableStartDate);
        this.timePickerEnd.value = `${this.openingHours[firstAvailableEndDate.getDay()].start
            .toString()
            .padStart(2, "0")}:00`;
        this.timePicker.disabled = true;
        this.checkboxShort.disabled = true;
    }

    /**
     * update ui
     * update cta properties dataset
     */
    updateUiAndProperties() {
        const startDate = this.picker.getStartDate().clone();
        if (!startDate) return;
        if (trigger.dataset.isAvailableInStore === "true") {
            this.timePicker.disabled = false;
        }
        // grab start
        const startDay = startDate.getDay();

        // no endDate set? use startDate
        let endDate = this.picker.getEndDate() || startDate.clone();

        // check if chosen range start or end is the same as an already booked range
        // Variant Booking Ranges are used for the variant picker (E-Bikes)
        const ranges = this.variantBookingRanges || this.bookingRanges;
        const previousBookingAtStartDate = ranges.find((r) => r.end.isSame(startDate));
        const nextBookingAtEndDate = ranges.find((r) => r.start.isSame(endDate));

        // add chosen time to dates
        // now we can grab the start hour
        const timepickerHour = +this.timePicker.value.split(":").shift();
        const pickupHour = Math.max(timepickerHour, previousBookingAtStartDate?.end.getHours() + lagTimeHours || 0);
        startDate.setHours(pickupHour);
        endDate.setHours(pickupHour);

        // calculate chosen range length
        const durationDays = endDate.diff(startDate);

        // calculate additional days (sun- and holidays directly following startDate)
        const additionalDays = this.getFirstAvailableDate(startDate).diff(startDate);

        // single day chosen... use startDate plus additionalDays as endDate
        if (durationDays === 0) {
            endDate = startDate.clone().add(additionalDays);
        }

        // can we choose short tariff?
        this.checkboxShort.disabled =
            durationDays > additionalDays || pickupHour + this.durationShortHours > this.openingHours[startDay].end;
        // reset short tariff if disabled and chosen
        if (this.checkboxShort.disabled && this.checkboxShort.checked) {
            this.checkboxShort.removeEventListener("change", this.select);
            this.checkboxShort.checked = false;
            this.checkboxShort.addEventListener("change", this.select);
        }

        // short tariff is chosen
        if (this.checkboxShort.checked) {
            endDate = new DateTime(startDate.toJSDate().valueOf() + this.durationShortMSeconds);
        }

        // adjust time picker according to start
        const earliestPickup =
            previousBookingAtStartDate?.end.getHours() + lagTimeHours || this.openingHours[startDay].start;
        this.disableTimePickerOptions(startDay, earliestPickup);

        // endDate determined... grab
        const endDay = endDate.getDay();
        const latestReturn = nextBookingAtEndDate?.start.getHours() - lagTimeHours || this.openingHours[endDay].end;

        // set end time value
        const earliestReturnHour = this.checkboxShort.checked
            ? pickupHour + this.durationShortHours
            : this.openingHours[endDay].start;
        const returnHour = clamp(Math.min(latestReturn, pickupHour), earliestReturnHour, this.openingHours[endDay].end);
        this.timePickerEnd.value = `${returnHour.toString().padStart(2, "0")}:${endDate
            .getMinutes()
            .toString()
            .padEnd(2, "0")}`;
        endDate.setHours(returnHour);

        // update picker
        this.start = startDate;
        this.end = endDate;
        this.picker.setDateRange(startDate, endDate);

        this.warning = false;
        if (previousBookingAtStartDate || nextBookingAtEndDate) {
            this.warning = true;
            this.toastMessage = window.i18n.rental.pdp_lockedPartly_hint;
        }
        if (endDay === 6 && returnHour !== pickupHour) {
            this.warning = true;
            this.toastMessage = window.i18n.rental.pdp_adjusted_return_hour_hint;
        }
        if (this.showWarningToastMessage && this.toastMessage) {
            this.showWarning();
            this.showWarningToastMessage = false;
        }

        // update cta dataset
        this.properties = {
            ...this.properties,
            from: this.getFormattedDateTime(startDate),
            to: this.getFormattedDateTime(endDate),
            _insurance_quantity: this.end.diff(this.start) + 1,
            _accessories_quantity: Math.min(this.end.diff(this.start), 7)
        };
    }

    /**
     * disable unavailable start time options
     * disabled if:
     *  value is smaller than opening-hours start
     *  value is greater than opening-hours end
     *  short tariff chosen and value is greater than opening-hours end min duration short hours
     */
    private disableTimePickerOptions(startDay: number, startHour: number) {
        const minStart = Math.max(this.openingHours[startDay].start, startHour);
        this.timePicker.options.forEach((option: HTMLOptionElement) => {
            const value = parseInt(option.dataset.value);
            const maxEnd = this.openingHours[startDay].end - (this.checkboxShort.checked ? this.durationShortHours : 0);
            option.disabled = value < minStart || value > maxEnd;
        });

        if (this.timePicker.options[this.timePicker.selectedIndex].disabled) {
            this.timePicker.value = `${minStart}:00`;
        }
    }

    /**
     * obtain first available date after given date (skip sun- and holidays)
     * @param startDate
     */
    getFirstAvailableDate(startDate: DateTime): DateTime {
        const loopDate = startDate.clone();
        do {
            loopDate.add(1);
        } while (regionsForHolidays.find((r) => isSunOrHoliday(loopDate, r)));
        return loopDate;
    }

    /**
     * obtain 24 hour date time formatted string
     * @param d
     */
    getFormattedDateTime(d: DateTime): string {
        return `${d.format(DATE_FORMATS.date)} ${d.getHours().toString().padStart(2, "0")}:${d
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
    }

    /**
     * load product from API
     */
    async loadProduct() {
        this.product = await this.api.fetchProduct(trigger.dataset.product);
        return this;
    }

    /**
     * load booking from API
     */
    async loadBookingRanges() {
        this.bookingRanges = (await this.api.fetchBookingDates(trigger.dataset.product)).map((b) => {
            return {
                variant_id: b.variant_id,
                start: new DateTime(new Date(b.starts_at)),
                end: new DateTime(new Date(b.finishes_at))
            };
        });
        return this;
    }

    /**
     * sort booking ranges by start
     */
    sortBookingRanges() {
        this.bookingRanges = this.bookingRanges.sort((a, b) => {
            if (a.start < b.start) return -1;
            if (a.start > b.start) return 1;
            return 0;
        });
        return this;
    }

    /**
     * check if next range start day is same as current range end day
     * extend booking end until end of day in that case to prevent overbooking
     */
    extendRangeEnd() {
        this.bookingRanges.forEach((range, idx) => {
            if (this.bookingRanges[idx + 1]) {
                const dayAfter = range.end.clone().add(1).format(DATE_FORMATS.date);
                if (dayAfter === this.bookingRanges[idx + 1].start.format(DATE_FORMATS.date)) {
                    range.end.setHours(this.openingHours[range.end.getDay()].end);
                }
            }
        });
        return this;
    }

    /**
     * create an array of DateTime objects where the product is booked
     */
    createBookedDatesArray() {
        this.bookedDates = [];
        this.bookingRanges.forEach((b) => {
            let next = b.start.clone().add(1);
            this.bookedDates.push(b.start);
            while (next.isBefore(b.end)) {
                this.bookedDates.push(next);
                next = next.clone().add(1);
            }
            this.bookedDates.push(b.end);
        });
        return this;
    }

    /**
     * obtain dDateTime object from formatted string
     * @param d
     */
    getDateTimeFromFormattedString(d: string): DateTime {
        return new DateTime(new Date(d));
    }

    /**
     * set start dataset if value is instance of DateTime
     * delete dataset otherwise
     * @param v
     */
    set start(v: DateTime | any) {
        if (v instanceof DateTime) {
            trigger.dataset.start = v.format(DATE_FORMATS.date);
            const dayShort = v.toJSDate().toLocaleString(this.locale, { weekday: "short" });
            this.startPresentation.dataset.date = `${dayShort}. ${v.format(DATE_FORMATS.presentation)}`;
        } else {
            delete trigger.dataset.start;
            delete this.startPresentation.dataset.date;
        }
    }

    /**
     * obtain start date time
     */
    get start(): DateTime {
        return new DateTime(trigger.dataset.start, DATE_FORMATS.date);
    }

    /**
     * set end dataset if value is instance of DateTime
     * delete dataset otherwise
     * @param v
     */
    set end(v: DateTime | any) {
        if (v instanceof DateTime) {
            trigger.dataset.end = v.format(DATE_FORMATS.date);
            const dayShort = v.toJSDate().toLocaleString(this.locale, { weekday: "short" });
            this.endPresentation.dataset.date = `${dayShort}. ${v.format(DATE_FORMATS.presentation)}`;
        } else {
            delete trigger.dataset.end;
            delete this.endPresentation.dataset.date;
        }
    }

    /**
     * obtain end date time
     */
    get end(): DateTime {
        return new DateTime(trigger.dataset.end, DATE_FORMATS.date);
    }

    /**
     * set selected variant dataset
     * @param v
     */
    set variant(v: string) {
        this.cta.dataset.variant = v;
    }

    /**
     * obtain quantity from dataset
     */
    get quantity(): number {
        return parseInt(this.cta.dataset.quantity) || 0;
    }

    /**
     * set selected quantity dataset
     * @param v
     */
    set quantity(v: number) {
        this.cta.dataset.quantity = v.toString();
    }

    /**
     * obtain properties from dataset
     */
    get properties() {
        try {
            return JSON.parse(this.cta.dataset.properties);
        } catch (error) {
            return {};
        }
    }

    /**
     * set/delete properties dataset
     * @param v
     */
    set properties(v: IRentalProperties) {
        if (Object.keys(v).length > 0) {
            v._rental_pickup = this.cta.dataset.rentalPickup;
            this.cta.dataset.properties = JSON.stringify(v);
        } else {
            delete this.cta.dataset.properties;
        }
    }

    set price(v: number) {
        this.priceBox.innerHTML = "";
        if (v > 0) {
            const content = this.priceTemplate.content.cloneNode(true) as HTMLSpanElement;
            content.querySelector<HTMLSpanElement>(".amount").innerHTML = (v / 100).toString();
            const durationContainer = content.querySelector<HTMLSpanElement>(".duration");
            const pricePerDayContainer = content.querySelector<HTMLSpanElement>(".price-per-day");
            if (this.properties._insurance_quantity === 1) {
                // we assume short tariff to be selected if insurance duration -eq 1
                durationContainer.remove();
                pricePerDayContainer.remove();
            } else {
                durationContainer.querySelector<HTMLSpanElement>("span").innerHTML =
                    this.properties._rental_duration.toString();
                durationContainer.innerHTML = `${durationContainer.innerHTML} ${
                    durationContainer.dataset[this.properties._rental_duration > 1 ? "plural" : "single"]
                }`;
                pricePerDayContainer.querySelector<HTMLSpanElement>("span").innerHTML = formatMoneyIntl(
                    this.properties._price_per_day
                );
            }
            content.querySelector<HTMLSpanElement>(".tariff span").innerHTML = this.properties._tariff;
            this.priceBox.appendChild(content);
        }
    }

    /**
     * en/disable cta button
     * @param v
     */
    set disabledCta(v: boolean) {
        this.cta.disabled = !!v;
    }

    /**
     * obtain variants from dataset
     */
    get variants() {
        return this.detailsWrapper.dataset.variants.split(",").map((v) => {
            const parts = v.split(":");
            return {
                tariff: parts[0],
                days: parts[0].match(/\d/) ? parseInt(v.split(":")[0].replace(/\D/g, "")) : 0,
                id: parts[1]
            };
        });
    }

    /**
     * obtain prices from dataset
     */
    get prices() {
        return this.detailsWrapper.dataset.prices.split(",").map((v) => {
            const parts = v.split(":");
            return {
                tariff: parts[0],
                days: parts[0].match(/\d/) ? parseInt(v.split(":")[0].replace(/\D/g, "")) : 0,
                price: parseInt(parts[1])
            };
        });
    }

    set warning(v: boolean) {
        this.indicatorWarning.classList.toggle("active", !!v);
    }

    get isProductAvailable(): boolean {
        return this.product?.length > 0;
    }

    private showWarning() {
        new Toast({
            title: window.i18n.general.hint_upcase,
            content: this.toastMessage,
            type: Toast.TYPE_INFO,
            autoCloseTimeout: 0
        })?.render();
    }

    /**
     * show general error toast message
     * @private
     */
    private showGeneralError() {
        new Toast({
            type: Toast.TYPE_ERROR,
            title: window.SuccessMessages.default.error,
            content: window.SuccessMessages.bookingRequest.error,
            autoCloseTimeout: 0
        });
    }

    /**
     * initialize picker element
     */
    initPicker() {
        trigger.classList.add("disabled");
        // amp plugin config causes false positiv error in idea IDE
        // @ts-ignore
        this.picker = new easepick.create({
            element: pickerElement,
            css: [pickerElement.dataset.cssPath],
            zIndex: 200000,
            grid: 2,
            calendars: 2,
            inline: false,
            readonly: false,
            autoApply: false,
            lang: pickerElement.dataset.lang,
            locale: {
                cancel: pickerElement.dataset.cancel,
                apply: pickerElement.dataset.apply
            },
            plugins: [AmpPlugin, RangePlugin, LockPartlyPlugin, BlackoutPlugin],
            // @ts-ignore
            AmpPlugin: {
                resetButton: true
            },
            RangePlugin: {
                tooltip: false,
                repick: true,
                strict: false
            },
            BlackoutPlugin: {
                minDate: new Date(),
                filter: (date: DateTime) =>
                    !date.inArray(this.bookedDates) &&
                    (date.inArray(holidayBlackouts) || date.isBefore(today) || date.isAfter(inAYear))
            },
            LockPartlyPlugin: {
                open: openingHours,
                lagTimeHours,
                minDate: today.isAfter(seasonStart) ? today : seasonStart,
                maxDate: seasonEnd,
                inseparable: true,
                ranges: this.bookingRanges,
                locale: JSON.parse(trigger.dataset.locale),
                filter: (date: DateTime) => date.inArray(this.bookedDates),
                blackouts: holidayBlackoutsAllYear
            },
            setup(picker) {
                picker.on("view", (e) => {
                    const { view, date, target } = e.detail;
                    if (view === "CalendarDay") {
                        addBlackoutTooltip(view, target, date);
                    }
                });

                picker.on("show", () => {
                    this.abortController = new AbortController();
                    preventScroll("blurred-bg");

                    document.documentElement.addEventListener(
                        "keyup",
                        () => {
                            picker.hide();
                        },
                        { signal: this.abortController.signal }
                    );
                });
                picker.on("hide", () => {
                    this.abortController.abort();
                    releaseScroll("blurred-bg");
                });
            }
        });

        if (pickedRange.start) {
            this.picker.setStartDate(new Date(pickedRange.start));
        }
        if (pickedRange.end) {
            this.picker.setEndDate(new Date(pickedRange.end));
        }
        if (pickedRange.start || pickedRange.end) {
            this.selectHandler();
        }

        if (this.picker) {
            this.picker.ui.container.classList.add("centered");
        }

        return this;
    }
}

if (document.body.classList.contains("motorbike-rental") && pickerElement && trigger) {
    new RentalDatePicker();
}
